import React, { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { AudienceCommentCard } from '@components/CommentCard/AudienceCommentCard';
import { getPlaceholderAvatarSrc } from '@components/main-components/avatar/utils';
import { HoverCard, StyledHoverContent } from '@components/main-components/hover-card';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType, TierNft } from '@graphql/reactQuery';
import { getFragment, gql, TopCommentCardFragmentDoc } from '@graphql/reactQuery';
import { TopAudienceSeatFragmentDoc } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import type { AudienceSortType } from './constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment TopCommentCard on ReleaseCollector {
    id
    user {
      id
      artist {
        id
      }
      username
      publicAddress
      webappUri
      avatar {
        id
        url
      }
    }
    nftsCount
    gaNftsCount
    vipNftsCount
    firstNftCollectedDate
    mostRecentNftWithComment {
      id
      comment {
        id
        message
      }
    }
  }
`);

export function Badge({
  hasGoldenEgg,
  type,
}: {
  hasGoldenEgg: boolean;
  type: 'audience' | 'hover_card';
}) {
  return (
    <div
      className={twMerge(
        'text-gradient-animated absolute z-above1 rounded-full p-[2px]',
        hasGoldenEgg ? 'golden-egg-border' : 'limited-border',
        type === 'audience' && 'right-[2px] top-[2px]',
        type === 'hover_card' && '-right-[2px] -top-[2px]',
      )}
    >
      <div
        className={twMerge(
          'flex h-[18px] w-[18px] items-center justify-center rounded-full bg-black p-[4px]',
        )}
      >
        <NextFutureImage
          alt={hasGoldenEgg ? 'golden egg badge' : 'limited badge'}
          src={hasGoldenEgg ? '/icons/only-egg.svg' : '/icons/only-star.svg'}
          className="!h-[10px] !w-[10px] !object-contain"
          width={10}
          height={10}
        />
      </div>
    </div>
  );
}

export function AudienceSeat({
  seatFrag,
  sort,
  tier,
}: {
  seatFrag: FragmentType<TopAudienceSeatFragmentDoc>;
  sort: AudienceSortType;
  tier: TierNft;
}) {
  const seat = getFragment(TopAudienceSeatFragmentDoc, seatFrag);

  const triggerRef = useRef<HTMLAnchorElement>(null);

  const {
    ownedGoldenEgg,
    vipNftsCount,
    user: { avatar, publicAddress, webappUri },
  } = seat;

  const { tablet } = useMediaQuery();

  const hasGoldenEgg = !!ownedGoldenEgg;
  // is VIP if has vipNftsCount and only GA filter is not selected
  const isVip = !!vipNftsCount && tier !== 'GA';

  // Only show badges on ALL and Limited Tabs
  const hasDecorators = (hasGoldenEgg || isVip) && tier !== 'GA';

  const userSeat = (
    <div
      className={twMerge(
        'relative aspect-square h-auto w-full min-w-0 bg-neutral100',
        'col-span-1 rounded-lg [&_img]:w-full [&_img]:object-cover',
      )}
    >
      <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
        {hasDecorators && <Badge hasGoldenEgg={hasGoldenEgg} type="audience" />}

        <NextFutureImage
          src={avatar?.url ?? getPlaceholderAvatarSrc({ walletAddress: publicAddress })}
          alt="user avatar"
          className="rounded-lg object-cover"
          placeholder="blur"
          blurDataURL={blurImage(400, 400)}
          fill
        />
      </AccessibleLink>
    </div>
  );

  if (!tablet) {
    return userSeat;
  }

  return (
    <HoverCard closeDelay={100}>
      <HoverCard.Trigger ref={triggerRef} asChild>
        {userSeat}
      </HoverCard.Trigger>

      <StyledHoverContent
        className="min-w-[253px] p-0"
        sideOffset={8}
        side="top"
        onPointerDownOutside={event => {
          if (event.target === triggerRef.current) event.preventDefault();
        }}
      >
        <TopCommentCard
          data={seat}
          edition={hasGoldenEgg ? 'goldenEgg' : isVip ? 'vip' : 'ga'}
          tier={tier}
          sort={sort}
        />
      </StyledHoverContent>
    </HoverCard>
  );
}

export const TopCommentCard = ({
  data,
  edition,
  tier,
  sort,
}: {
  data: FragmentType<TopCommentCardFragmentDoc>;
  edition: 'vip' | 'ga' | 'goldenEgg';
  tier: TierNft;
  sort: AudienceSortType;
}) => {
  const {
    user: { username, publicAddress, artist, webappUri, avatar },
    nftsCount,
    gaNftsCount,
    vipNftsCount,
    mostRecentNftWithComment,
    firstNftCollectedDate,
  } = getFragment(TopCommentCardFragmentDoc, data);

  return (
    <AudienceCommentCard
      username={username}
      comment={mostRecentNftWithComment?.comment?.message}
      date={firstNftCollectedDate}
      publicAddress={publicAddress}
      totalEditions={tier === 'ALL' ? nftsCount : tier === 'VIP' ? vipNftsCount : gaNftsCount}
      avatarUrl={avatar?.url}
      isArtist={artist != null}
      webappUri={webappUri}
      edition={edition}
      sort={sort}
    />
  );
};

const SkeletonSeat = () => (
  <div className="relative col-span-1 aspect-square h-auto w-full min-w-0 animate-pulse rounded-lg bg-neutral100" />
);

export const LoadingSeats = () => {
  return (
    <div className="flex w-full flex-col gap-2 md:gap-4">
      {/* Mobile and Desktop */}
      <div className="grid w-full grid-cols-[repeat(4,1fr)] gap-3 overflow-hidden">
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonSeat key={index} />
        ))}
      </div>

      {/* Desktop Only */}
      <div className="hidden w-full grid-cols-[repeat(6,1fr)] gap-3 overflow-hidden md:grid">
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonSeat key={index} />
        ))}
      </div>

      {/* Desktop Only */}
      <div className="hidden w-full grid-cols-[repeat(7,1fr)] gap-3 overflow-hidden md:grid">
        {Array.from({ length: 7 }).map((_, index) => (
          <SkeletonSeat key={index} />
        ))}
      </div>

      {/* Mobile only */}
      <div className="grid w-full grid-cols-[repeat(6,1fr)] gap-2 overflow-hidden md:hidden">
        {Array.from({ length: 12 }).map((_, index) => (
          <SkeletonSeat key={index} />
        ))}
      </div>
    </div>
  );
};
